import { resolveSlotSchema } from "@diality/contracts";
/**
 * Resolve a block's image slot, carrying one rendition per declared target.
 *
 * A slot that declares more than one target size holds one framing of the photo
 * per target — the Editor frames once per target, not once per page — and which
 * one a visitor receives is decided by the screen they are reading on. That
 * decision cannot be made here: this runs during SSR, where no screen exists.
 * So every framing travels to the renderer as `sources` and the browser picks
 * (see MediaSlotView).
 *
 * Deriving the framings rather than storing one ref each is sound because a
 * crop's id IS its output size (`{w}x{h}`, media.service.ts:377) and framing a
 * slot is all-or-nothing across its targets (frame-multi-target-slot's size
 * floor refuses a photo too small for any one of them). A filled slot is
 * therefore framed for EVERY target the component declares, so the asset the
 * ref names plus the declared targets name every framing exactly.
 *
 * A slot declaring one target is untouched — one framing, shown to everyone.
 */
export function resolveSlotMedia(block, slotKey, resolve) {
    const ref = block.media?.[slotKey];
    const primary = resolve(ref);
    // An unfilled slot, or a statically-composed block passing an absolute image
    // URL as its cropId — neither names a library asset with crops behind it.
    if (!primary || !ref?.assetId)
        return primary;
    const slot = resolveSlotSchema(block.componentId).mediaSlots.find((candidate) => candidate.key === slotKey);
    // A video has no renditions to choose between — it is served as uploaded —
    // so there are no per-target sources to derive, however many targets the slot
    // declares. What it does take from the slot is how it should behave, which is
    // the component's declaration and not the Editor's.
    if (primary.mediaType === "video") {
        return { ...primary, playback: slot?.playback ?? "controls" };
    }
    const targets = slot?.targets ?? [];
    if (targets.length < 2)
        return primary;
    const sources = [];
    for (const target of targets) {
        const framing = resolve({
            assetId: ref.assetId,
            cropId: `${target.width}x${target.height}`,
        });
        if (!framing)
            return primary;
        sources.push({
            url: framing.url,
            width: target.width,
            height: target.height,
        });
    }
    return { ...primary, sources };
}
//# sourceMappingURL=slot-media.js.map